/**
 * Warehouse Execution Components
 * PO management, staging checklist, and load-out confirmation.
 */
import { useEffect, useState } from "react";
import { PermissionGate } from "@/components/PermissionGate";
import { Link } from "react-router-dom";
import { useAppData } from "@/data/AppDataContext";
import { useVendorPO } from "@/data/VendorPOContext";
import type { PurchaseOrder, StagingRecord, LoadOutChecklist } from "@/data/warehouseExecution";
import { computeStagingStatus, isLoadOutComplete, isUuidLike, resolveSupplierName } from "@/data/warehouseExecution";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ShoppingCart, CheckCircle, Package, Truck, ClipboardCheck,
  ArrowRight, AlertTriangle, FileText, Clock, BoxSelect, User,
} from "lucide-react";

import { toast } from "sonner";
import { PaginationBar } from "@/components/PaginationBar";
import { usePagination } from "@/hooks/usePagination";

/* ═══════════════════════════════════════════
   PO Status Styles
   ═══════════════════════════════════════════ */
const PO_STATUS_STYLES: Record<string, string> = {
  Draft: "bg-muted text-muted-foreground",
  Ordered: "bg-info/10 text-info",
  "Partially Received": "bg-warning/10 text-warning",
  Received: "bg-success/10 text-success",
};

const STAGING_STATUS_STYLES: Record<string, string> = {
  "Not Staged": "bg-destructive/10 text-destructive",
  "Partially Staged": "bg-warning/10 text-warning",
  "Fully Staged": "bg-success/10 text-success",
};

/* ═══════════════════════════════════════════
    Purchase Order List & Management
    ═══════════════════════════════════════════ */
export function PurchaseOrderPanel() {
  const { purchaseOrders, setPurchaseOrders, jobs } = useAppData();
  const { vendors } = useVendorPO();

  const handleMarkOrdered = (poId: string) => {
    setPurchaseOrders((prev) =>
      prev.map((po) =>
        po.id === poId ? { ...po, status: "Ordered" as const, orderedAt: new Date().toISOString() } : po
      )
    );
    toast.success("PO marked as ordered");
  };

  const handleReceiveItem = (poId: string, sku: string) => {
    setPurchaseOrders((prev) =>
      prev.map((po) => {
        if (po.id !== poId) return po;
        const items = po.items.map((item) =>
          item.sku === sku ? { ...item, received: true, quantityReceived: item.quantityOrdered } : item
        );
        const allReceived = items.every((i) => i.received);
        const someReceived = items.some((i) => i.received);
        return {
          ...po,
          items,
          status: allReceived ? "Received" as const : someReceived ? "Partially Received" as const : po.status,
          receivedAt: allReceived ? new Date().toISOString() : po.receivedAt,
        };
      })
    );
    toast.success("Item received");
  };

  const handleReceiveAll = (poId: string) => {
    setPurchaseOrders((prev) =>
      prev.map((po) => {
        if (po.id !== poId) return po;
        return {
          ...po,
          items: po.items.map((i) => ({ ...i, received: true, quantityReceived: i.quantityOrdered })),
          status: "Received" as const,
          receivedAt: new Date().toISOString(),
        };
      })
    );
    toast.success("All items received");
  };

  if (purchaseOrders.length === 0) {
    return (
      <div className="py-10 text-center text-muted-foreground text-sm">
        <ShoppingCart className="h-8 w-8 mx-auto mb-2 text-muted-foreground/30" />
        No purchase orders yet — POs are auto-generated when quotes are won
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {purchaseOrders.map((po) => {
        const job = jobs.find((j) => j.id === po.jobId);
        const receivedCount = po.items.filter((i) => i.received).length;
        return (
          <Card key={po.id} className={`shadow-none ${po.status === "Draft" ? "border-warning/30" : ""}`}>
            <CardContent className="p-4">
              {/* PO Header */}
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-bold font-mono">{po.id}</span>
                    <span className={`inline-flex px-2 py-0.5 rounded text-[9px] font-bold ${PO_STATUS_STYLES[po.status]}`}>
                      {po.status}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mt-1 text-[10px] text-muted-foreground">
                    <Link to={`/jobs/${po.jobId}`} className="text-primary hover:underline font-medium">{po.jobId}</Link>
                    {job && <span>· {job.customerName}</span>}
                    <span>· {resolveSupplierName(po, vendors)}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  {po.status === "Draft" && (
                    <PermissionGate permission="warehouse_edit">
                      <Button size="sm" className="h-7 text-[10px] gap-1" onClick={() => handleMarkOrdered(po.id)}>
                        <Truck className="h-3 w-3" /> Mark Ordered
                      </Button>
                    </PermissionGate>
                  )}
                  {(po.status === "Ordered" || po.status === "Partially Received") && (
                    <PermissionGate permission="warehouse_edit">
                      <Button size="sm" variant="outline" className="h-7 text-[10px] gap-1" onClick={() => handleReceiveAll(po.id)}>
                        <CheckCircle className="h-3 w-3" /> Receive All
                      </Button>
                    </PermissionGate>
                  )}
                </div>
              </div>

              {/* Progress */}
              <div className="flex items-center gap-2 mb-3">
                <div className="flex-1 bg-muted rounded-full h-1.5">
                  <div
                    className="bg-primary h-1.5 rounded-full transition-all"
                    style={{ width: `${po.items.length ? (receivedCount / po.items.length) * 100 : 0}%` }}
                  />
                </div>
                <span className="text-[10px] text-muted-foreground font-medium">
                  {receivedCount}/{po.items.length} received
                </span>
              </div>

              {/* Items */}
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-muted/40 border-b">
                      <th className="text-left py-2 px-3 font-medium text-muted-foreground">Item</th>
                      <th className="text-center py-2 px-3 font-medium text-muted-foreground">Qty</th>
                      <th className="text-center py-2 px-3 font-medium text-muted-foreground">Status</th>
                      <th className="py-2 px-3 w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {po.items.map((item) => (
                      <tr key={item.sku} className={`border-b last:border-0 ${item.received ? "bg-success/5" : ""}`}>
                        <td className="py-2 px-3">
                          <p className="font-medium">{item.itemName}</p>
                          <p className="text-[10px] text-muted-foreground font-mono">{item.sku}</p>
                        </td>
                        <td className="py-2 px-3 text-center">{item.quantityOrdered}</td>
                        <td className="py-2 px-3 text-center">
                          {item.received ? (
                            <span className="text-[10px] font-semibold text-success">✓ Received</span>
                          ) : (
                            <span className="text-[10px] font-semibold text-warning">Pending</span>
                          )}
                        </td>
                        <td className="py-2 px-3">
                          {!item.received && po.status !== "Draft" && (
                            <Checkbox
                              checked={false}
                              onCheckedChange={() => handleReceiveItem(po.id, item.sku)}
                            />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Timestamps */}
              <div className="flex gap-3 mt-2 text-[10px] text-muted-foreground">
                <span>Created: {new Date(po.createdAt).toLocaleDateString()}</span>
                {po.orderedAt && <span>Ordered: {new Date(po.orderedAt).toLocaleDateString()}</span>}
                {po.receivedAt && <span>Received: {new Date(po.receivedAt).toLocaleDateString()}</span>}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

/* ═══════════════════════════════════════════
   Staging Checklist Panel
   ═══════════════════════════════════════════ */
export function StagingPanel() {
  const { stagingRecords, setStagingRecords, jobs } = useAppData();
  const stagingRecordsPage = usePagination(stagingRecords, 10);

  useEffect(() => {
    stagingRecordsPage.reset();
  }, [stagingRecords.length]);

  const handleToggleItem = (jobId: string, sku: string) => {
    setStagingRecords((prev) =>
      prev.map((sr) => {
        if (sr.jobId !== jobId) return sr;
        const items = sr.items.map((i) =>
          i.sku === sku ? { ...i, staged: !i.staged } : i
        );
        return { ...sr, items, status: computeStagingStatus(items) };
      })
    );
  };

  const handleStageAll = (jobId: string) => {
    setStagingRecords((prev) =>
      prev.map((sr) => {
        if (sr.jobId !== jobId) return sr;
        const items = sr.items.map((i) => ({ ...i, staged: true }));
        return { ...sr, items, status: "Fully Staged" as const, stagedAt: new Date().toISOString() };
      })
    );
    toast.success("All items staged");
  };

  if (stagingRecords.length === 0) {
    return (
      <div className="py-10 text-center text-muted-foreground text-sm">
        <BoxSelect className="h-8 w-8 mx-auto mb-2 text-muted-foreground/30" />
        No staging records yet
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {stagingRecordsPage.paged.map((sr) => {
        const job = jobs.find((j) => j.id === sr.jobId);
        const stagedCount = sr.items.filter((i) => i.staged).length;
        return (
          <Card key={sr.jobId} className="shadow-none">
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2">
                    <Link to={`/jobs/${sr.jobId}`} className="text-xs font-bold font-mono text-primary hover:underline">{sr.jobId}</Link>
                    <span className={`inline-flex px-2 py-0.5 rounded text-[9px] font-bold ${STAGING_STATUS_STYLES[sr.status]}`}>
                      {sr.status}
                    </span>
                  </div>
                  {job && <p className="text-[10px] text-muted-foreground mt-0.5">{job.customerName}</p>}
                </div>
                {sr.status !== "Fully Staged" && (
                  <Button size="sm" variant="outline" className="h-7 text-[10px] gap-1" onClick={() => handleStageAll(sr.jobId)}>
                    <CheckCircle className="h-3 w-3" /> Stage All
                  </Button>
                )}
              </div>

              {/* Progress */}
              <div className="flex items-center gap-2 mb-3">
                <div className="flex-1 bg-muted rounded-full h-1.5">
                  <div
                    className="bg-primary h-1.5 rounded-full transition-all"
                    style={{ width: `${sr.items.length ? (stagedCount / sr.items.length) * 100 : 0}%` }}
                  />
                </div>
                <span className="text-[10px] text-muted-foreground font-medium">
                  {stagedCount}/{sr.items.length} staged
                </span>
              </div>

              {/* Items */}
              <div className="space-y-1">
                {sr.items.map((item) => (
                  <div
                    key={item.sku}
                    className={`flex items-center gap-3 px-3 py-2 rounded-lg border transition-colors cursor-pointer ${
                      item.staged ? "bg-success/5 border-success/20" : "hover:bg-muted/30"
                    }`}
                    onClick={() => handleToggleItem(sr.jobId, item.sku)}
                  >
                    <Checkbox checked={item.staged} />
                    <div className="flex-1 min-w-0">
                      <p className={`text-xs font-medium ${item.staged ? "line-through text-muted-foreground" : ""}`}>{item.itemName}</p>
                      <p className="text-[10px] text-muted-foreground font-mono">{item.sku}</p>
                    </div>
                    {item.staged && <CheckCircle className="h-3.5 w-3.5 text-success shrink-0" />}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        );
      })}
      <PaginationBar {...stagingRecordsPage} />
    </div>
  );
}

/* ═══════════════════════════════════════════
   Load-Out Checklist Panel
   ═══════════════════════════════════════════ */
export function LoadOutPanel() {
  const { loadOutChecklists, setLoadOutChecklists, jobs } = useAppData();
  const [signOffName, setSignOffName] = useState("");
  const loadOutPage = usePagination(loadOutChecklists, 10);

  useEffect(() => {
    loadOutPage.reset();
  }, [loadOutChecklists.length]);

  const handleToggle = (jobId: string, field: keyof LoadOutChecklist) => {
    setLoadOutChecklists((prev) =>
      prev.map((cl) => {
        if (cl.jobId !== jobId) return cl;
        const updated = { ...cl, [field]: !cl[field] };
        updated.complete = isLoadOutComplete(updated);
        return updated;
      })
    );
  };

  const handleSignOff = (jobId: string) => {
    if (!signOffName.trim()) {
      toast.error("Enter name to sign off");
      return;
    }
    setLoadOutChecklists((prev) =>
      prev.map((cl) =>
        cl.jobId === jobId
          ? { ...cl, signedOffBy: signOffName.trim(), signedOffAt: new Date().toISOString(), complete: true }
          : cl
      )
    );
    setSignOffName("");
    toast.success("Load-out signed off");
  };

  if (loadOutChecklists.length === 0) {
    return (
      <div className="py-10 text-center text-muted-foreground text-sm">
        <ClipboardCheck className="h-8 w-8 mx-auto mb-2 text-muted-foreground/30" />
        No load-out checklists yet
      </div>
    );
  }

  const checklistItems: { field: keyof LoadOutChecklist; label: string; icon: React.ElementType }[] = [
    { field: "allMaterialsLoaded", label: "All materials loaded on vehicle", icon: Package },
    { field: "correctItemsVerified", label: "Correct items verified against list", icon: CheckCircle },
    { field: "toolsReady", label: "Tools and equipment ready", icon: ClipboardCheck },
    { field: "specialInstructionsReviewed", label: "Special instructions reviewed", icon: FileText },
  ];

  return (
    <div className="space-y-3">
      {loadOutPage.paged.map((cl) => {
        const job = jobs.find((j) => j.id === cl.jobId);
        const completedCount = checklistItems.filter((ci) => cl[ci.field] as boolean).length;
        const allChecked = completedCount === checklistItems.length;
        return (
          <Card key={cl.jobId} className={`shadow-none ${cl.complete ? "border-success/30" : ""}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2">
                    <Link to={`/jobs/${cl.jobId}`} className="text-xs font-bold font-mono text-primary hover:underline">{cl.jobId}</Link>
                    {cl.complete ? (
                      <Badge className="bg-success/10 text-success border-success/30 text-[9px]">
                        <CheckCircle className="h-2.5 w-2.5 mr-0.5" /> Complete
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-[9px]">
                        {completedCount}/{checklistItems.length} checked
                      </Badge>
                    )}
                  </div>
                  {job && <p className="text-[10px] text-muted-foreground mt-0.5">{job.customerName}</p>}
                </div>
              </div>

              {/* Checklist items */}
              <div className="space-y-1 mb-3">
                {checklistItems.map((ci) => {
                  const checked = cl[ci.field] as boolean;
                  return (
                    <div
                      key={ci.field}
                      className={`flex items-center gap-3 px-3 py-2 rounded-lg border transition-colors cursor-pointer ${
                        checked ? "bg-success/5 border-success/20" : "hover:bg-muted/30"
                      }`}
                      onClick={() => !cl.signedOffBy && handleToggle(cl.jobId, ci.field)}
                    >
                      <Checkbox checked={checked} disabled={!!cl.signedOffBy} />
                      <ci.icon className={`h-3.5 w-3.5 shrink-0 ${checked ? "text-success" : "text-muted-foreground"}`} />
                      <span className={`text-xs ${checked ? "line-through text-muted-foreground" : "font-medium"}`}>{ci.label}</span>
                    </div>
                  );
                })}
              </div>

              {/* Sign-off */}
              {allChecked && !cl.signedOffBy && (
                <div className="flex items-center gap-2 pt-2 border-t">
                  <User className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  <Input
                    placeholder="Your name to sign off"
                    className="h-7 text-xs flex-1"
                    value={signOffName}
                    onChange={(e) => setSignOffName(e.target.value)}
                  />
                  <PermissionGate permission="warehouse_edit">
                    <Button size="sm" className="h-7 text-[10px] gap-1" onClick={() => handleSignOff(cl.jobId)}>
                      <CheckCircle className="h-3 w-3" /> Sign Off
                    </Button>
                  </PermissionGate>
                </div>
              )}

              {cl.signedOffBy && (
                <div className="flex items-center gap-2 pt-2 border-t text-[10px] text-muted-foreground">
                  <CheckCircle className="h-3 w-3 text-success" />
                  <span>Signed off by <span className="font-semibold">{cl.signedOffBy}</span> on {new Date(cl.signedOffAt!).toLocaleString()}</span>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
      <PaginationBar {...loadOutPage} />
    </div>
  );
}

/* ═══════════════════════════════════════════
   Job-level PO + Staging Summary (for JobDetail)
   ═══════════════════════════════════════════ */
export function JobWarehouseExecution({ jobId }: { jobId: string }) {
  const { purchaseOrders, setPurchaseOrders, stagingRecords, setStagingRecords, loadOutChecklists, setLoadOutChecklists } = useAppData();

  const po = purchaseOrders.find((p) => p.jobId === jobId);
  const staging = stagingRecords.find((s) => s.jobId === jobId);
  const loadOut = loadOutChecklists.find((c) => c.jobId === jobId);

  if (!po && !staging && !loadOut) return null;

  const handleReceiveItem = (sku: string) => {
    if (!po) return;
    setPurchaseOrders((prev) =>
      prev.map((p) => {
        if (p.id !== po.id) return p;
        const items = p.items.map((i) => i.sku === sku ? { ...i, received: true, quantityReceived: i.quantityOrdered } : i);
        const allReceived = items.every((i) => i.received);
        return { ...p, items, status: allReceived ? "Received" as const : "Partially Received" as const };
      })
    );
  };

  const handleMarkOrdered = () => {
    if (!po) return;
    setPurchaseOrders((prev) =>
      prev.map((p) => p.id === po.id ? { ...p, status: "Ordered" as const, orderedAt: new Date().toISOString() } : p)
    );
    toast.success("PO marked as ordered");
  };

  const handleToggleStaging = (sku: string) => {
    if (!staging) return;
    setStagingRecords((prev) =>
      prev.map((sr) => {
        if (sr.jobId !== jobId) return sr;
        const items = sr.items.map((i) => i.sku === sku ? { ...i, staged: !i.staged } : i);
        return { ...sr, items, status: computeStagingStatus(items) };
      })
    );
  };

  return (
    <div className="space-y-4">
      {/* PO Summary */}
      {po && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
              <ShoppingCart className="h-3 w-3" /> Purchase Order
            </p>
            <div className="flex items-center gap-2">
              <span className={`inline-flex px-2 py-0.5 rounded text-[9px] font-bold ${PO_STATUS_STYLES[po.status]}`}>
                {po.status}
              </span>
              {po.status === "Draft" && (
                <Button size="sm" variant="outline" className="h-6 text-[9px] gap-1" onClick={handleMarkOrdered}>
                  <Truck className="h-2.5 w-2.5" /> Order
                </Button>
              )}
            </div>
          </div>
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-xs">
              <tbody>
                {po.items.map((item) => (
                  <tr key={item.sku} className={`border-b last:border-0 ${item.received ? "bg-success/5" : ""}`}>
                    <td className="py-1.5 px-3 font-medium">{item.itemName}</td>
                    <td className="py-1.5 px-3 text-center text-muted-foreground">×{item.quantityOrdered}</td>
                    <td className="py-1.5 px-3 text-center">
                      {item.received ? (
                        <span className="text-[10px] text-success font-semibold">✓</span>
                      ) : po.status !== "Draft" ? (
                        <Checkbox checked={false} onCheckedChange={() => handleReceiveItem(item.sku)} />
                      ) : (
                        <span className="text-[10px] text-warning font-semibold">Needs Order</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Staging Summary */}
      {staging && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
              <BoxSelect className="h-3 w-3" /> Staging
            </p>
            <span className={`inline-flex px-2 py-0.5 rounded text-[9px] font-bold ${STAGING_STATUS_STYLES[staging.status]}`}>
              {staging.status}
            </span>
          </div>
          <div className="space-y-1">
            {staging.items.map((item) => (
              <div
                key={item.sku}
                className={`flex items-center gap-2 px-3 py-1.5 rounded border text-xs cursor-pointer transition-colors ${
                  item.staged ? "bg-success/5 border-success/20" : "hover:bg-muted/30"
                }`}
                onClick={() => handleToggleStaging(item.sku)}
              >
                <Checkbox checked={item.staged} />
                <span className={`flex-1 ${item.staged ? "line-through text-muted-foreground" : "font-medium"}`}>{item.itemName}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Load-out status */}
      {loadOut && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg border bg-muted/20">
          <ClipboardCheck className={`h-3.5 w-3.5 ${loadOut.complete ? "text-success" : "text-muted-foreground"}`} />
          <span className="text-xs font-medium">
            Load-out: {loadOut.complete ? (
              <span className="text-success">Complete{loadOut.signedOffBy ? ` — ${loadOut.signedOffBy}` : ""}</span>
            ) : (
              <span className="text-muted-foreground">Pending</span>
            )}
          </span>
        </div>
      )}
    </div>
  );
}
